import React, { useEffect, useState } from 'react';
import { Button, Form, Container, Row, Col, Card } from 'react-bootstrap';
import { BiPlus } from 'react-icons/bi';
import { MdDeleteOutline, MdArrowBack } from 'react-icons/md';
import AsyncSelect from 'react-select/async';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    createBrand,
    editBrand,
    getBrandDetails,
    deleteBrandCategory
} from '../../api/context/brand';

import { createSizeChart, getSizeChart, getSizeTypesAndSizes } from '../../api/context/sizechart';
import { getCategoryListingPaginate, getSubCategoryListingPaginate } from '../../api/context/manufacturer';
import SizeChartModal from '../../components/Modal/SizeChartModal';
import Loader from '../../components/loader/loader';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import imageToBase64 from 'image-to-base64/browser';
import { IoMdArrowBack } from 'react-icons/io';

const EditBrandPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [sizeChartMeta, setSizeChartMeta] = useState({ sizes: [], sizeTypes: [] });
    const [sizeChartData, setSizeChartData] = useState({});
    const [isSavingSizeChart, setIsSavingSizeChart] = useState(false);
    const [showSizeChartModal, setShowSizeChartModal] = useState(false);
    const [category, setCategory] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState({});
    const [isFocusedStaff, setIsFocusedStaff] = useState(false);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState(new Set());

    // Formik Setup
    const formik = useFormik({
        initialValues: {
            name: '',
            image: '',
            image_view: '',
            categories: [],
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Brand name is required'),
            image: Yup.mixed().nullable(),
            categories: Yup.array().of(
                Yup.object().shape({
                    categoryId: Yup.string().required('Category is required'),
                    subCategories: Yup.array().min(1, 'At least one subcategory is required'),
                })
            ),
        }),
        onSubmit: async (values) => {
            setIsSavingSizeChart(true);

            // Build size chart payload
            const sizeChartPayload = buildSizeChartPayload(sizeChartData, sizeChartMeta);
            const sizeChartValues = sizeChartPayload.values;

            const payload = {
                ...values,
                sizeChartValues,
                ...(id && { id })
            };

            // Remove image_view from payload
            delete payload.image_view;

            try {
                if (id) {
                    // Update existing brand
                    await editBrand(payload)((response) => {
                        if (response?.data?.statusCode === 200) {
                            toast.success("Brand Updated Successfully");
                            navigate('/brand');
                        } else {
                            toast.error(response?.data?.message);
                        }
                    });
                } else {
                    // Create new brand
                    await createBrand(payload)((response) => {
                        if (response?.data?.statusCode === 200) {
                            toast.success("Brand Created Successfully");
                            navigate('/brand');
                        } else {
                            toast.error(response?.data?.message);
                        }
                    });
                }
            } catch (error) {
                toast.error("An error occurred while saving the brand");
            } finally {
                setIsSavingSizeChart(false);
            }
        },
    });

    const handleImageChange = async (e) => {
        const file = e?.target?.files[0];
        const maxSizeInBytes = 2 * 1024 * 1024;
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];

        if (file?.size > maxSizeInBytes) {
            toast.error("File size exceeds 2 MB limit");
            e.target.value = null;
            return;
        }

        if (!validImageTypes.includes(file?.type)) {
            toast.error("Only jpg/jpeg and png files are allowed!");
            e.target.value = null;
            return;
        }

        const response = await imageToBase64(URL.createObjectURL(file));
        formik.setValues({
            ...formik.values,
            image: response,
            image_view: 'data:image/jpeg;base64,' + response,
        });
    };

    const getAllSubCategoryIds = () => {
        return formik.values.categories.flatMap((cat) =>
            (cat.selectedSubCategory || []).map((sub) => sub.value)
        );
    };

    const mapSizeChartArrayToObject = (apiData) => {
        const result = {};
        apiData.forEach((item) => {
            const categoryId = String(item.brandCategory.categoryId);
            const subCategoryId = String(item.brandCategory.subCategoryId);
            const size = String(item.subsubCategorySize?.size ?? '');
            const sizeType = String(item.subsubCategorySizeType?.sizeType ?? '');
            const measurement = item.measurement;

            if (!result[categoryId]) result[categoryId] = {};
            if (!result[categoryId][subCategoryId]) result[categoryId][subCategoryId] = {};
            if (!result[categoryId][subCategoryId][size]) result[categoryId][subCategoryId][size] = {};

            result[categoryId][subCategoryId][size][sizeType] = measurement;
        });
        return result;
    };

    const fetchSizeChart = () => {
        if (!id) return;

        getSizeChart({ brandId: id })((response) => {
            if (response?.data?.data) {
                const mapped = mapSizeChartArrayToObject(response.data.data);
                setSizeChartData(mapped);
            } else {
                setSizeChartData({});
            }
        });
    };

    const fetchAvailableCategories = () => {
        getCategoryListingPaginate({
            page: 1,
            limit: 100,
            search: ''
        })((response) => {
            if (response?.statusCode === 200) {
                setAvailableCategories(response.results);
            }
        });
    };

    const fetchBrandData = async () => {
        if (!id) {
            setIsLoading(false);
            fetchAvailableCategories();
            return;
        }

        getBrandDetails({ id })((response) => {
            if (response?.data?.statusCode === 200) {
                const brandData = response.data.data;

                // Group subcategories by categoryId
                const categoryMap = new Map();

                brandData.BrandCategories?.forEach(item => {
                    const categoryId = item.categoryId;

                    if (!categoryMap.has(categoryId)) {
                        categoryMap.set(categoryId, {
                            categoryId: categoryId,
                            subCategories: [],
                            selectedCategory: {
                                value: item.categoryId,
                                label: item.category?.title || `Category ${item.categoryId}`
                            },
                            selectedSubCategory: []
                        });
                    }

                    // Add subcategory to the existing category
                    const category = categoryMap.get(categoryId);
                    const subCategoryOption = {
                        value: item.subCategoryId,
                        label: item.subcategory?.title || `Subcategory ${item.subCategoryId}`
                    };

                    // Avoid duplicates
                    if (!category.subCategories.includes(item.subCategoryId)) {
                        category.subCategories.push(item.subCategoryId);
                        category.selectedSubCategory.push(subCategoryOption);
                    }
                });

                // Convert map values to array
                const transformedCategories = Array.from(categoryMap.values());

                formik.setValues({
                    name: brandData.name || '',
                    image: '',
                    image_view: brandData.image || '',
                    categories: transformedCategories,
                });

                // Update selected category IDs
                const selectedIds = new Set(transformedCategories.map(cat => cat.categoryId));
                setSelectedCategoryIds(selectedIds);

                // Pre-fetch subcategories for each selected category
                transformedCategories.forEach(cat => {
                    fetchSubcategoriesForCategory(cat.categoryId);
                });

                setIsLoading(false);
                fetchSizeChart();
                fetchAvailableCategories();
            } else {
                toast.error("Failed to fetch brand details");
                setIsLoading(false);
            }
        });
    };

    // Fetch categories dynamically based on input
    const loadOptionsCategory = (inputValue) => {
        return new Promise((resolve) => {
            const typingTimer = setTimeout(() => {
                if (inputValue.length >= 2) {
                    getCategoryListingPaginate({
                        page: 1,
                        limit: 10,
                        search: inputValue
                    })((response) => {
                        if (response?.statusCode === 200) {
                            resolve(response.results);
                            setCategory(response.results);
                        } else {
                            resolve([]);
                        }
                    });
                } else {
                    resolve([]);
                }
            }, 300);
        });
    };

    // Fetch subcategories for a specific category
    const fetchSubcategoriesForCategory = (categoryId) => {
        getSubCategoryListingPaginate({
            page: 1,
            pageSize: 100,
            search: '',
            categoryIds: [categoryId],
        })((response) => {
            if (response?.statusCode === 200) {
                setSubcategoryOptions(prev => ({
                    ...prev,
                    [categoryId]: response.results
                }));
            }
        });
    };

    // Fetch subcategories based on category ID for AsyncSelect
    const loadOptionsSubCategory = (inputValue, categoryId) => {
        return new Promise((resolve) => {
            // If we already have subcategories for this category, filter them
            if (subcategoryOptions[categoryId]) {
                const filtered = subcategoryOptions[categoryId].filter(option =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                );
                resolve(filtered);
                return;
            }

            // If we don't have subcategories yet, fetch them
            getSubCategoryListingPaginate({
                page: 1,
                pageSize: 100,
                search: inputValue,
                categoryIds: [categoryId],
            })((response) => {
                if (response?.statusCode === 200) {
                    setSubcategoryOptions(prev => ({
                        ...prev,
                        [categoryId]: response.results
                    }));
                    resolve(response.results);
                } else {
                    resolve([]);
                }
            });
        });
    };

   const handleCategorySelect = (category) => {
    if (selectedCategoryIds.has(category.value)) {
        return; // Already selected
    }

    const newCategory = {
        categoryId: category.value,
        subCategories: [], // Initialize as empty array
        selectedCategory: category,
        selectedSubCategory: [], // Initialize as empty array
    };

    formik.setFieldValue('categories', [
        ...formik.values.categories,
        newCategory,
    ]);

    setSelectedCategoryIds(prev => new Set([...prev, category.value]));

    // Immediately fetch subcategories for the selected category
    fetchSubcategoriesForCategory(category.value);
};

    const deleteCategory = (categoryId) => {
        if (id) {
            // If editing existing brand, call API to delete category
            // Note: You might need to adjust your API to delete all subcategories for this category
            deleteBrandCategory(id, categoryId)((response) => {
                if (response?.data?.statusCode === 200) {
                    toast.success("Category removed successfully");
                } else {
                    toast.error("Failed to remove category");
                }
            });
        }

        // Remove from local form state
        const updatedCategories = formik.values.categories.filter(
            cat => cat.categoryId !== categoryId
        );
        formik.setFieldValue('categories', updatedCategories);

        // Update selected category IDs
        setSelectedCategoryIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(categoryId);
            return newSet;
        });
    };

    const customNoOptionsMessage = ({ inputValue }) => {
        return inputValue.length === 0 ? "Type to search subcategories" : "No options found";
    };

    const buildSizeChartPayload = (sizeChartData, sizeChartMeta) => {
        const values = [];
        Object.entries(sizeChartData).forEach(([categoryId, subCats]) => {
            Object.entries(subCats).forEach(([subCategoryId, sizesObj]) => {
                const sizes = [];
                Object.entries(sizesObj).forEach(([sizeLabel, typeObj]) => {
                    Object.entries(typeObj).forEach(([typeLabel, measurement]) => {
                        const size = sizeChartMeta.sizes.find(
                            s => s.size === sizeLabel && String(s.subsubcategoryId) === String(subCategoryId)
                        );
                        const type = sizeChartMeta.sizeTypes.find(
                            t => t.sizeType === typeLabel && String(t.subsubcategoryId) === String(subCategoryId)
                        );

                        if (size && type && measurement !== "") {
                            sizes.push({
                                subsubCategorySizeId: size.id,
                                subsubCategorySizeTypeId: type.id,
                                measurement: Number(measurement),
                                unit: size.unit || "cm"
                            });
                        }
                    });
                });

                if (sizes.length > 0) {
                    values.push({
                        categoryId: Number(categoryId),
                        subCategoryId: Number(subCategoryId),
                        sizes
                    });
                }
            });
        });

        return { values };
    };

    const handleBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        fetchBrandData();
    }, [id]);

    useEffect(() => {
        const subCategoryIds = getAllSubCategoryIds();
        const allIds = subCategoryIds.join(',');

        if (subCategoryIds.length) {
            getSizeTypesAndSizes([allIds])((response) => {
                if (response?.data) {
                    setSizeChartMeta({
                        sizes: response.data.sizes,
                        sizeTypes: response.data.sizeTypes,
                    });
                }
            });
        } else {
            setSizeChartMeta({ sizes: [], sizeTypes: [] });
        }
    }, [formik.values.categories]);

    if (isLoading) {
        return (
            <div style={{
                height: '300px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Loader />
            </div>
        );
    }

    return (
        <>
            <Container fluid className="py-4">
                <Row className="mb-4 align-items-center">
                    <Col>
                        <div className="d-flex align-items-center mb-0">
                            <div
                                onClick={() => navigate(-1)}
                                style={{ cursor: 'pointer', fontSize: '24px', fontWeight: '800', display: 'flex', alignItems: 'center', marginRight: '15px' }}
                            >
                                <IoMdArrowBack className='icon' />
                            </div>
                            <h4 className="mb-0 fw-bold">{id ? 'Update' : 'Add'} Brand</h4>
                        </div>
                    </Col>
                    { (
                        <Col xs="auto">
                            <Button
                                variant="outline-dark"
                                onClick={() => setShowSizeChartModal(true)}
                                 className="subcategory-add-button"
                            >
                                <BiPlus className="me-1" />
                                Edit Size Chart
                            </Button>
                        </Col>
                    )}
                </Row>

                {/* Brand Details Card */}
                <Card className="mb-4">
                    <Card.Body>
                        <Form onSubmit={formik.handleSubmit}>
                            <Row>
                                <Col lg={8}>
                                    {/* Brand Name */}
                                    <Form.Group className="mb-4" controlId="formBrandName">
                                        <Form.Label className="fw-bold">Brand Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter brand name"
                                            name="name"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            isValid={formik.touched.name && !formik.errors.name}
                                            isInvalid={formik.touched.name && !!formik.errors.name}
                                            className="brand-name-input"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formik.errors.name}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    {/* Available Categories */}
                                    <div className="mb-2">
                                        <Form.Label className="fw-bold">Available Categories</Form.Label>
                                        <div className="available-categories-container d-flex flex-wrap gap-2">
                                            {availableCategories.map((category) => (
                                                <div
                                                    key={category.value}
                                                    className={`category-pill ${selectedCategoryIds.has(category.value) ? 'selected' : ''}`}
                                                    onClick={() => handleCategorySelect(category)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        borderRadius: '20px',
                                                        border: '1px solid #dee2e6',
                                                        backgroundColor: selectedCategoryIds.has(category.value) ? '#f8f9fa' : 'white',
                                                        color: selectedCategoryIds.has(category.value) ? '#000' : '#6c757d',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        fontWeight: selectedCategoryIds.has(category.value) ? '600' : '400',
                                                        boxShadow: selectedCategoryIds.has(category.value) ? '0 0 0 2px rgba(0,0,0,0.1)' : 'none'
                                                    }}
                                                >
                                                    {category.label}
                                                    {selectedCategoryIds.has(category.value) && (
                                                        <span className="check-mark ms-2">✓</span>
                                                    )}
                                                </div>
                                            ))}

                                        </div>
                                    </div>

                                    {/* Selected Categories with Subcategories */}

                                    <div style={{
                                        marginTop: "30px",

                                    }}>

                                        <Form.Label className="fw-bold">Selected Categories</Form.Label>
                                        {formik.values.categories.length > 0 ? (

                                            <div style={{

                                                
                                                // padding: "16px",


                                                background: "#ffffffff",
                                                // border: "1px solid #dee2e6",

                                                borderRadius: "8px",

                                                color: "#6c757d",
                                                fontSize: "14px",
                                            }}>

                                                <Row>
                                                    {formik.values.categories.map((cat, index) => (
                                                        <Row className="my-2" key={index}>
                                                            <Form.Group>
                                                                <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                                                                    <span className="fw-semibold text-dark">{cat.selectedCategory?.label}</span>
                                                                    <MdDeleteOutline
                                                                        role="button"
                                                                        onClick={() => deleteCategory(cat.categoryId)}
                                                                        style={{ fontSize: '20px', color: 'red' }}
                                                                        className="delete_category_btn"
                                                                    />
                                                                </Form.Label>
                                                                <AsyncSelect
                                                                    onFocus={() => setIsFocusedStaff(true)}
                                                                    onBlur={() => setIsFocusedStaff(false)}
                                                                    loadOptions={(inputValue) =>
                                                                        loadOptionsSubCategory(inputValue, cat.categoryId)
                                                                    }
                                                                    defaultOptions={subcategoryOptions[cat.categoryId] || []}
                                                                    classNamePrefix="select"
                                                                    isClearable={true}
                                                                    placeholder="select subcategories"
                                                                    onChange={(selectedOption) => {
                                                                        const updatedCategories = [...formik.values.categories];
                                                                        updatedCategories[index] = {
                                                                            ...updatedCategories[index],
                                                                            subCategories: selectedOption ?
                                                                                selectedOption.map(opt => opt.value) : [],
                                                                            selectedSubCategory: selectedOption,
                                                                        };
                                                                        formik.setFieldValue('categories', updatedCategories);
                                                                    }}
                                                                    value={cat.selectedSubCategory || []}
                                                                    noOptionsMessage={customNoOptionsMessage}
                                                                    isMulti
                                                                    styles={{
                                                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                                                        control: (base) => ({
                                                                            ...base,
                                                                            border: "1px solid #ced4da",
                                                                            borderRadius: "8px",
                                                                            minHeight: "40px",
                                                                            fontSize: "13px",
                                                                        }),
                                                                        valueContainer: (base) => ({
                                                                            ...base,
                                                                            display: "flex",
                                                                            flexWrap: "nowrap",
                                                                            overflowX: "auto",
                                                                            overflowY: "hidden",
                                                                            scrollbarWidth: "none",
                                                                            msOverflowStyle: "none",
                                                                            "::-webkit-scrollbar": {
                                                                                display: "none",
                                                                            },
                                                                        }),
                                                                        input: (base) => ({
                                                                            ...base,
                                                                            minWidth: "80px",
                                                                            flexShrink: 0,
                                                                        }),
                                                                        multiValue: (base) => ({
                                                                            ...base,
                                                                            backgroundColor: "#288d9dff", // purple
                                                                            color: "white",
                                                                            borderRadius: "12px",
                                                                            padding: "0 4px",
                                                                            flexShrink: 0,
                                                                        }),
                                                                        multiValueLabel: (base) => ({
                                                                            ...base,
                                                                            color: "white",
                                                                            fontSize: "12px",
                                                                        }),
                                                                        multiValueRemove: (base) => ({
                                                                            ...base,
                                                                            color: "white",
                                                                            ':hover': {
                                                                                backgroundColor: "#288d9dff",
                                                                                color: "white",
                                                                            },
                                                                        }),
                                                                    }}
                                                                />
                                                                {/* <div style={{ color: '#DC3544', fontSize: "12px" }}>
                                                                {formik.errors.categories?.[index]?.subCategories}
                                                            </div> */}
                                                            </Form.Group>
                                                        </Row>
                                                    ))}
                                                </Row>
                                            </div>
                                        ) : <div
                                            style={{

                                                height: "200px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                textAlign: "center",
                                                background: "#ffffffff",
                                                border: "1px solid #dee2e6",
                                                borderRadius: "8px",
                                                padding: "10px",
                                                color: "#6c757d",
                                                fontSize: "14px",
                                            }}
                                        >
                                            No categories selected yet.
                                        </div>
                                        }


                                    </div>

                                </Col>




                                <Col lg={4}>
                                    {/* Brand Image Upload */}
                                    <div className="brand-image-upload-container">
                                        <Form.Label className="fw-bold">Brand Image</Form.Label>
                                        <div className="image-upload-wrapper">
                                            <input
                                                type="file"
                                                id="brand-image-upload"
                                                onChange={handleImageChange}
                                                className="image-upload-input"
                                                accept="image/jpeg,image/png,image/gif"
                                            />
                                            <label htmlFor="brand-image-upload" className="image-upload-label">
                                                {formik.values.image_view ? (
                                                    <div className="image-preview-container">
                                                        <img
                                                            src={formik.values.image_view}
                                                            alt="Brand"
                                                            className="brand-image-preview"
                                                            style={{ width: '100%', height: '200px', objectFit: 'contain' }}
                                                        />
                                                        <div className="image-overlay">
                                                            <BiPlus className="overlay-icon" />
                                                            <span>Change Image</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="image-placeholder" style={{
                                                        height: '200px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        border: '2px dashed #dee2e6',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer'
                                                    }}>
                                                        <BiPlus className="placeholder-icon" style={{ fontSize: '24px', marginBottom: '8px' }} />
                                                        <span>Upload Brand Image</span>
                                                       
                                                    </div>
                                                )}
                                            </label>
                                        </div>
                                        <Form.Control.Feedback type="invalid" className="d-block">
                                            {formik.errors.image}
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>



                {/* Buttons outside the card */}
                <div className="d-flex gap-2 justify-content-end mt-4">
                    <Button
                        className="cancel_btn"
                        variant="outline-secondary"
                        onClick={handleBack}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="request_btn"
                        type="button"
                        onClick={formik.handleSubmit}
                        disabled={isSavingSizeChart}
                        style={{ backgroundColor: '#000', borderColor: '#000' }}
                    >
                        {isSavingSizeChart ? 'Saving...' : (id ? 'Update Brand' : 'Create Brand')}
                    </Button>
                </div>
            </Container>

            <SizeChartModal
                
                show={showSizeChartModal}
                handleClose={() => setShowSizeChartModal(false)}
                categories={formik.values.categories}
                sizeChartData={sizeChartData}
                setSizeChartData={setSizeChartData}
                loading={isSavingSizeChart}
                sizeOptions={sizeChartMeta.sizes}
                sizeTypes={sizeChartMeta.sizeTypes}
                selectedBrandId={id}
            />
        </>
    );
};

export default EditBrandPage;